import React, { useState } from 'react';
import './style.css';
import { Laugh, Smile, Meh, Frown, ArrowLeft, ArrowRight, MoveUp } from 'lucide-react';
import CalculateRequiredIncomeToAfford from '/src/services/CalculateRequiredIncomeToAfford';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import { Doughnut, Line } from 'react-chartjs-2';
import HomeValueVsRequiredIncomeChart from '../../HomeValueVsRequiredIncomeChart/HomeValueVsRequiredIncomeChart';
import LTVCalculators from '/src/services/LTVCalculators';

const LTVAttractivenessLookingAtMarket = ({
    currentStageData,
    originalValues,
    currentHomeValue,
    setCurrentHomeValue,
    interestRatePercent,
    setInterestRatePercent,
    downPaymentPercent,
    setDownPaymentPercent,
    handleReset,
    incomePercentages,
    stage3IncomeDisplayArray,
    fipsChartData,
    allRequiredIncomesAbove200k,
    incomeHigherInCounty,
    medianIncomeGrowingFasterInCounty,
    meanIncomeGrowingFasterInCounty
}) => {

    const [showAssumptions, setShowAssumptions] = useState(false);

    // Update the options in both Doughnut components to remove data labels
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: { family: 'inherit', weight: 'bold' },
                    padding: 20,
                    boxWidth: 25,
                    usePointStyle: true
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.raw;
                        return `${(value * 100).toFixed(1)}%`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        cutout: '50%'
    };
    const stage_3_sentence_options = {
        first_a: {
            fips_higher: "It looks like income levels are higher in the county than your zip code,",
            zip_higher: "It looks like income levels are higher in your zip code than in the county,"
        },
        first_b: {
            higher_faster_too: "and are growing faster too.",
            fips_higher_slower: "though not growing as fast across both median and mean income.",
            zip_higher_faster_slow: "though not growing as fast across both median and mean income."
        },
        second_a: {
            all_required_incomes_above_200k: "Since all of the required incomes we calculated for this home are above $200K, you're betting on the upper end of the market. The question is, as costs continue to rise, will even that pool of buyers begin to dry up?",
            not_all_required_incomes_above_200k: "Since some of the required incomes we calculated for this home are below $200K, you're able to be attractive to a wider range of buyers. The question is, as costs continue to rise, will the pool of buyers begin to shrink?"
        },

    }

    let stage3_first_conclusion_sentence_a;
    let stage3_first_conclusion_sentence_b;
    if (incomeHigherInCounty) {
        stage3_first_conclusion_sentence_a = stage_3_sentence_options.first_a.fips_higher;

        if (medianIncomeGrowingFasterInCounty && meanIncomeGrowingFasterInCounty) {
            stage3_first_conclusion_sentence_b = stage_3_sentence_options.first_b.higher_faster_too;
        } else if (medianIncomeGrowingFasterInCounty || meanIncomeGrowingFasterInCounty) {
            stage3_first_conclusion_sentence_b = stage_3_sentence_options.first_b.fips_higher_slower;
        } else {
            stage3_first_conclusion_sentence_b = stage_3_sentence_options.first_b.zip_higher_faster_slow;
        }
    } else {
        stage3_first_conclusion_sentence_a = stage_3_sentence_options.first_a.zip_higher;

        if (!medianIncomeGrowingFasterInCounty && !meanIncomeGrowingFasterInCounty) {
            stage3_first_conclusion_sentence_b = stage_3_sentence_options.first_b.zip_higher_faster_slow;
        } else if (medianIncomeGrowingFasterInCounty || meanIncomeGrowingFasterInCounty) {
            stage3_first_conclusion_sentence_b = stage_3_sentence_options.first_b.zip_higher_faster_slow;
        }
    }

    let stage3_second_conclusion_sentence_a;
    let stage3_second_conclusion_sentence_b;
    if (allRequiredIncomesAbove200k) {
        stage3_second_conclusion_sentence_a = stage_3_sentence_options.second_a.all_required_incomes_above_200k;
    } else {
        stage3_second_conclusion_sentence_a = stage_3_sentence_options.second_a.not_all_required_incomes_above_200k;
    }

    const stage3_conclusion_sentence = `${stage3_first_conclusion_sentence_a} ${stage3_first_conclusion_sentence_b} ${stage3_second_conclusion_sentence_a}`

    const introText = "Here is the breakdown of households (i.e. potential future buyers!) in your area across household income, alongside the required income to purchase this home we just examined:"


    return (
        <div className="ltv-attractiveness-content">
            <h2 className="ltv-attractiveness-title">{currentStageData.title}</h2>
            <p className="ltv-attractiveness-description text-align-center" dangerouslySetInnerHTML={{ __html: introText }} />
            <div className="ltv-attractiveness-income-charts">
                <div className="ltv-attractiveness-county-zip-income-charts">
                    {fipsChartData && stage3IncomeDisplayArray.map(({ location, chart_data, income_data, total_households }) => (
                        <div key={location} className="ltv-attractiveness-income-chart-container">
                            <div className="ltv-attractiveness-chart-header">
                                <span className="ltv-attractiveness-chart-header-location">{location}</span>
                                {total_households && (
                                    <span className="ltv-attractiveness-chart-header-total-households">
                                        {total_households} Households
                                    </span>
                                )}
                            </div>
                            <Doughnut
                                data={chart_data}
                                options={chartOptions}
                            />
                            <div className="ltv-attractiveness-income-stats">
                                <div className="ltv-attractiveness-income-stats-group-header">
                                    Household Incomes
                                </div>
                                <div key={location} className="ltv-attractiveness-income-stat-group">
                                    {income_data.map((data, index) => (
                                        <div className="ltv-attractiveness-income-stat" key={index}>
                                            <span className="income-label">
                                                {index === 0 ? 'Median' : 'Mean'}
                                            </span>
                                            <span className="income-value text-align-center">
                                                ${data.value_today.toLocaleString()}
                                            </span>
                                            <div className="income-growth">
                                                <div className="income-growth-icon">
                                                    <MoveUp className="ltv-attractiveness-icon" />
                                                </div>
                                                {(data.growth_rate * 100).toFixed(1)}%
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="ltv-attractiveness-results-column market">
                    <span className="ltv-attractiveness-headline text-align-center">
                        Required Income for Different Levels of Housing Spend
                    </span>
                    <div className="ltv-attractiveness-percentage-grid market">
                        {incomePercentages.map(({ icon: Icon, percentage, level, requiredIncome }) => (
                            <div key={percentage} className="ltv-attractiveness-percentage-item-frame">
                                <div key={percentage} className={`ltv-attractiveness-percentage-item ${level}`}>
                                    <Icon className="ltv-attractiveness-icon" />
                                    <span className="ltv-attractiveness-percentage">{percentage}</span>
                                </div>
                                <span className="ltv-attractiveness-income">
                                    {Number(requiredIncome).toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 0
                                    })}
                                </span>
                            </div>
                        ))}
                    </div>
                    <button className="ltv-attractiveness-show-assumptions-button" onClick={() => setShowAssumptions(!showAssumptions)}>
                        {showAssumptions ? 'Hide Assumptions' : 'Change Assumptions'}
                    </button>
                </div>
            </div>
            {showAssumptions && (

                <div className="ltv-attractiveness-assumptions-column market">

                    <div className="ltv-attractiveness-assumptions">
                        <div className="ltv-attractiveness-input-group">
                            <div className="ltv-attractiveness-input-group-header">
                                <label>Home Value</label>
                                <span className="ltv-attractiveness-input-value">
                                    {LTVCalculators.formatCurrency(currentHomeValue)}
                                </span>
                            </div>
                            <div className="ltv-attractiveness-input-wrapper">
                                <input
                                    type="range"
                                    min={Math.round((originalValues.currentHomeValue / 2) / 25000) * 25000}
                                    max={Math.round((originalValues.currentHomeValue * 1.5) / 25000) * 25000}
                                    step={25000}
                                    value={currentHomeValue}
                                    onChange={(e) => {
                                        setCurrentHomeValue(Number(e.target.value));
                                    }}
                                    className="ltv-attractiveness-slider"
                                />
                            </div>
                        </div>
                        <div className="ltv-attractiveness-input-group">
                            <div className="ltv-attractiveness-input-group-header">
                                <label>Interest Rate</label>
                                <span className="ltv-attractiveness-input-value">
                                    {(interestRatePercent * 100).toFixed(1)}%
                                </span>
                            </div>
                            <div className="ltv-attractiveness-input-wrapper">
                                <input
                                    type="range"
                                    min={0}
                                    max={10}
                                    step={0.5}
                                    value={interestRatePercent * 100}
                                    onChange={(e) => {
                                        setInterestRatePercent(Number(e.target.value) / 100);
                                    }}
                                    className="ltv-attractiveness-slider"
                                />
                            </div>
                        </div>
                        <div className="ltv-attractiveness-input-group">
                            <div className="ltv-attractiveness-input-group-header">
                                <label>Buyer Puts Down</label>
                                <span className="ltv-attractiveness-input-value">
                                    {(downPaymentPercent * 100).toFixed(0)}%
                                </span>
                            </div>
                            <div className="ltv-attractiveness-input-wrapper">
                                <input
                                    type="range"
                                    min={0}
                                    max={50}
                                    step={5}
                                    value={downPaymentPercent * 100}
                                    onChange={(e) => {
                                        setDownPaymentPercent(Number(e.target.value) / 100);
                                    }}
                                    className="ltv-attractiveness-slider"
                                />
                            </div>
                        </div>
                    </div>
                    {(currentHomeValue !== originalValues.currentHomeValue ||
                        interestRatePercent !== originalValues.interestRate ||
                        downPaymentPercent !== originalValues.downPayment) && (
                            <button
                                className="ltv-attractiveness-reset-button"
                                onClick={handleReset}
                            >
                                Reset to Original Values
                            </button>
                        )}
                </div>
            )}
            {/* <CurlyBraceDivider direction='right' color='var(--dark-grey)' />
                <div className="ltv-attractiveness-results-column">
                <span className="ltv-attractiveness-headline text-align-center">
                        Based on the current expenses for this home and the percentage of their household income the buyer is willing to spend on housing, this is the income the buyer would need:
                    </span>
                    <div className="ltv-attractiveness-percentage-grid">
                        {incomePercentages.map(({ icon: Icon, percentage, level, requiredIncome }) => (
                            <div key={percentage} className="ltv-attractiveness-percentage-item-frame">
                                <div key={percentage} className={`ltv-attractiveness-percentage-item ${level}`}>
                                    <Icon className="ltv-attractiveness-icon" />
                                    <span className="ltv-attractiveness-percentage">{percentage}</span>
                                </div>
                                <span className="ltv-attractiveness-income">
                                    {Number(requiredIncome).toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                        maximumFractionDigits: 0
                                    })}
                                </span>
                            </div>
                        ))}
                    </div>
                </div> */}
            {/* <p className="ltv-attractiveness-description text-align-center">
                {currentStageData.description}
            </p> */}

            <p className="ltv-attractiveness-description text-align-center" dangerouslySetInnerHTML={{ __html: stage3_conclusion_sentence }} />
        </div>
    );
};

export default LTVAttractivenessLookingAtMarket;